/**
 * Authentication Context
 * Manages user authentication state and provides auth utilities
 */

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authApiService } from '../services/authApi';

/**
 * Authentication action types
 */
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  SET_USER: 'SET_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

/**
 * Initial authentication state
 */
const initialState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true, // Start as true to handle initial token check from storage
  error: null,
};

/**
 * Authentication reducer
 */
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
      };

    case AUTH_ACTIONS.REFRESH_TOKEN:
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload.user,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

/**
 * Authentication Context
 */
const AuthContext = createContext(null);

/**
 * Token storage utilities
 */
const TOKEN_STORAGE_KEY = 'auth_token';
const REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';
const USER_STORAGE_KEY = 'user_data';

const tokenStorage = {
  getToken: () => {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  },

  setToken: token => {
    try {
      if (token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
      } else {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      }
    } catch {
      // Handle storage errors silently
    }
  },

  getRefreshToken: () => {
    try {
      return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  },

  setRefreshToken: refreshToken => {
    try {
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      }
    } catch {
      // Handle storage errors silently
    }
  },

  getUser: () => {
    try {
      const userData = localStorage.getItem(USER_STORAGE_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  },

  setUser: user => {
    try {
      if (user) {
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(USER_STORAGE_KEY);
      }
    } catch {
      // Handle storage errors silently
    }
  },

  clearAll: () => {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
    } catch {
      // Handle storage errors silently
    }
  },

  clearTokens: () => {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    } catch {
      // Handle storage errors silently
    }
  },

  clearUser: () => {
    try {
      localStorage.removeItem(USER_STORAGE_KEY);
    } catch {
      // Handle storage errors silently
    }
  },
};

/**
 * Authentication Provider Component
 */
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from storage on mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const token = tokenStorage.getToken();
        const user = tokenStorage.getUser();
        
        if (token && user) {
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user,
              token,
              refreshToken: tokenStorage.getRefreshToken() || token,
            },
          });
        } else {
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login function
   */
  const login = async (identifier, password, rememberMe = false) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      // Debug logging
      console.log('🔐 Login attempt:', {
        identifier,
        password: password ? '***' : 'undefined',
        rememberMe
      });

      // Use real API login
      const response = await authApiService.login({
        username: identifier, // Backend expects username field
        password: password
      });
      
      const { user, token, permissions: serverPermissions, companyId } = response;
      const userWithPermissions = {
        ...user,
        companyId: companyId || user?.companyId,
        permissions: Array.isArray(serverPermissions) ? serverPermissions : (user?.permissions || [])
      };
      
      // Store tokens and user data
      tokenStorage.setToken(token);
      tokenStorage.setRefreshToken(token);
      tokenStorage.setUser(userWithPermissions);

      // Explicitly redundant storage for user-requested individual fields if needed elsewhere
      localStorage.setItem('role', userWithPermissions.role);
      localStorage.setItem('companyId', String(userWithPermissions.companyId));
      localStorage.setItem('username', userWithPermissions.username);

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: userWithPermissions,
          token,
          refreshToken: token, // Use main token as refresh token for now
        },
      });

      return { success: true, data: response };
    } catch (error) {
      const errorMessage = error.message || 'Login failed';

      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: {
          error: errorMessage,
        },
      });

      return { success: false, error: errorMessage };
    }
  };

  /**
   * Logout function
   */
  const logout = async () => {
    try {
      // Call logout API if token exists
      if (state.token) {
        await authApiService.logout();
      }
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
    } finally {
      // Clear storage and state
      tokenStorage.clearAll();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  /**
   * Refresh token function
   */
  const refreshAuthToken = async () => {
    try {
      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authApiService.refreshToken(refreshToken);
      const { token: newToken, refreshToken: newRefreshToken } = response.data;

      // Update stored tokens
      tokenStorage.setToken(newToken);
      tokenStorage.setRefreshToken(newRefreshToken);

      dispatch({
        type: AUTH_ACTIONS.REFRESH_TOKEN,
        payload: {
          token: newToken,
          refreshToken: newRefreshToken,
        },
      });

      return { success: true, token: newToken };
    } catch (error) {
      // If refresh fails, logout user
      logout();
      return { success: false, error: 'Session expired' };
    }
  };

  /**
   * Update user profile
   */
  const updateUser = userData => {
    tokenStorage.setUser(userData);
    dispatch({
      type: AUTH_ACTIONS.SET_USER,
      payload: {
        user: userData,
      },
    });
  };

  /**
   * Clear authentication error
   */
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  /**
   * Check if user has specific permission
   */
  const hasPermission = permission => {
    if (!state.user || !state.user.permissions) {
      return false;
    }
    return state.user.permissions.includes(permission);
  };

  /**
   * Check if user has specific role
   */
  const hasRole = role => {
    if (!state.user || !state.user.role) {
      return false;
    }
    return state.user.role === role;
  };

  /**
   * Get user's full name
   */
  const getUserDisplayName = () => {
    if (!state.user) return 'Guest';
    return state.user.name || state.user.email || 'User';
  };

  const contextValue = {
    // State
    ...state,

    // Actions
    login,
    logout,
    refreshAuthToken,
    updateUser,
    clearError,

    // Utilities
    hasPermission,
    hasRole,
    getUserDisplayName,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

/**
 * Custom hook to use authentication context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
